/* eslint-disable max-lines */
import React, {
    useEffect, useState
} from 'react';
import {
    useParams
} from 'react-router-dom';
import {
    Button
} from 'shared/components/ui/button';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from 'shared/components/ui/dialog';
import {
    Loader2, Plus, Pencil, Globe, Check, RefreshCw, AlertCircle
} from 'lucide-react';
import {
    cn
} from 'shared/lib/utils';
import {
    apiManagerService
} from 'shared/api/services/BEX/apiManager.service';
import {
    ApiForm
} from 'builders/builderInternal/components/ApiManager/ApiForm';
import {
    Skeleton
} from 'shared/components/ui/skeleton';

/**
 * ApiPicker — chip with Change / Edit / + New popups for an API ref on
 * an opcode `api` step. Drop-in replacement for the bare Combobox in
 * V2StepAPI. Re-uses the existing M51 ApiForm (805 LOC) inside a Dialog —
 * no fork. Saves through `apiManagerService` so anything created/edited
 * shows up in Library → APIs automatically.
 *
 * Props:
 *   apiId (string)            — currently selected API _id
 *   selectedApi (object?)     — pre-resolved API record from parent (avoids id flash)
 *   onChange ({_id, ...api})  — fires with the picked / saved API
 */
export default function ApiPicker({
    apiId, selectedApi = null, onChange, editTrigger = 0
}) {
    const {
        oragID, subOragID
    } = useParams();
    const [list, setList] = useState([]);
    const [loadingList, setLoadingList] = useState(true);
    const [error, setError] = useState(null);

    const [pickerOpen, setPickerOpen] = useState(false);
    const [formOpen, setFormOpen] = useState(false);
    const [formMode, setFormMode] = useState('new'); // 'new' | 'edit'
    const [formInitial, setFormInitial] = useState(null);
    const [busy, setBusy] = useState(false);

    async function refresh() {
        if (!oragID || !subOragID) return;
        setLoadingList(true);
        setError(null);
        try {
            const res = await apiManagerService.listApis(oragID, subOragID);
            const arr = res?.data || res || [];
            setList(Array.isArray(arr) ? arr : []);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to load APIs.');
        } finally {
            setLoadingList(false);
        }
    }

    useEffect(() => { refresh(); }, [oragID, subOragID]);

    const selected = selectedApi ?? list.find(a => a._id === apiId) ?? null;
    const chipLoading = !!apiId && !selected && loadingList;

    // Allow parent components (InlineAPIStep) to trigger the edit dialog
    // when the user clicks "Add response schema" from a banner.
    useEffect(() => {
        if (!editTrigger) return;
        if (!selected) return;
        setFormInitial(prepareForEdit(selected));
        setFormMode('edit');
        setFormOpen(true);
    }, [editTrigger]); // eslint-disable-line

    function openPicker() { refresh(); setPickerOpen(true); }
    function openNew() {
        setFormInitial(blankApi());
        setFormMode('new');
        setFormOpen(true);
    }
    function openEdit() {
        if (!selected) return;
        setFormInitial(prepareForEdit(selected));
        setFormMode('edit');
        setFormOpen(true);
    }

    function pickFromList(api) {
        onChange({
            _id: api._id,
            ...api
        });
        setPickerOpen(false);
    }

    /**
     * ApiForm calls onSave(apiData) with form-shaped data; we persist via
     * apiManagerService and then bubble the new/updated record up.
     */
    async function handleFormSave(formData) {
        setBusy(true);
        setError(null);
        try {
            const apiObj = {
                organizationId: oragID,
                subOrganizationId: subOragID,
                name: formData.name,
                url: formData.url,
                timeout: formData.timeout,
                method: formData.method,
                headers: formData.headers,
                paramsDef: formData.paramsDef,
                auth: formData.auth || { type: 'none' },
            };
            if (formData.input_schema) apiObj.input_schema = formData.input_schema;
            if (formData.output_schema) apiObj.output_schema = formData.output_schema;

            if (formMode === 'edit' && formData._id) {
                apiObj.apiId = formData._id;
            }
            const res = await apiManagerService.persistApiWithVaultAuth(
                apiObj,
                formData._vaultSecrets || {},
                formData._headerVaultSecrets || {},
                formData._keepSecretHeaderNames || [],
            );
            const saved = res?.data || res;
            // Refresh list, select the saved record.
            await refresh();
            if (saved?._id) onChange({
                _id: saved._id,
                ...saved
            });
            setFormOpen(false);
        } catch (err) {
            setError(err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Save failed.');
        } finally {
            setBusy(false);
        }
    }

    return (
        <div>
            <div className="ds-pick-chip">
                <span className="ds-pick-chip-icon ds-tone-blue">
                    <Globe size={15} />
                </span>
                <div className="ds-pick-chip-body">
                    {chipLoading ? (
                        <ApiChipSkeleton />
                    ) : selected ? (
                        <>
                            <div className="ds-pick-chip-title">{selected.name || selected._id}</div>
                            <div className="ds-pick-chip-meta">
                                <span style={{
                                    fontFamily: 'var(--ds-font-mono)',
                                    fontWeight: 600
                                }}>{selected.method}</span>
                                {selected.url ? ` · ${selected.url}` : ''}
                            </div>
                        </>
                    ) : apiId ? (
                        <>
                            <div className="ds-pick-chip-title" style={{
                                fontFamily: 'var(--ds-font-mono)'
                            }}>{apiId}</div>
                            <div className="ds-pick-chip-meta" style={{
                                fontStyle: 'italic'
                            }}>not in your library</div>
                        </>
                    ) : (
                        <div className="ds-pick-chip-meta" style={{
                            fontStyle: 'italic'
                        }}>No API selected.</div>
                    )}
                </div>
                <div className="ds-pick-chip-actions">
                    <Button variant="outline" size="sm" type="button" onClick={openPicker}>Change</Button>
                    <Button
                        variant="outline"
                        size="sm"
                        type="button"
                        onClick={openEdit}
                        disabled={!selected}
                        title={selected ? 'Edit this API' : 'Pick or create one first'}
                    >
                        <Pencil className="mr-1 h-3 w-3" /> Edit
                    </Button>
                    <Button size="sm" type="button" onClick={openNew}>
                        <Plus className="mr-1 h-3 w-3" /> New
                    </Button>
                </div>
            </div>
            <p className="ds-help" style={{
                marginTop: 6
            }}>
                Saved into your sub-org's Library — visible under <b>Library → APIs</b>.
            </p>
            {error && (
                <div className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-900">
                    <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <div>{error}</div>
                </div>
            )}

            {/* Picker dialog */}
            <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Pick an API</DialogTitle>
                        <DialogDescription>
                            Lists every API in this sub-org's Library. Pick one or create new.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={refresh} disabled={loadingList}>
                            <RefreshCw className={cn('mr-1 h-3 w-3', loadingList && 'animate-spin')} /> Refresh
                        </Button>
                        <Button size="sm" onClick={() => { setPickerOpen(false); openNew(); }}>
                            <Plus className="mr-1 h-3 w-3" /> New API
                        </Button>
                        <span className="ml-auto text-xs text-muted-foreground">{list.length} in library</span>
                    </div>
                    <div className="max-h-[60vh] overflow-y-auto rounded border">
                        {loadingList && (
                            <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
                                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                            </div>
                        )}
                        {!loadingList && list.length === 0 && (
                            <div className="p-6 text-center text-sm text-muted-foreground">
                                No APIs in this sub-org yet.
                            </div>
                        )}
                        {!loadingList && list.map((api) => {
                            const sel = api._id === apiId;
                            return (
                                <button
                                    key={api._id}
                                    type="button"
                                    onClick={() => pickFromList(api)}
                                    className={cn('ds-pick-row', sel && 'ds-pick-row-selected')}
                                >
                                    <span className="ds-pick-row-icon ds-tone-blue">
                                        <Globe size={14} />
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="truncate font-semibold text-sm">{api.name || api._id}</span>
                                            <span className="ds-badge ds-badge-info" style={{
                                                fontFamily: 'var(--ds-font-mono)'
                                            }}>
                                                {api.method || '—'}
                                            </span>
                                            {sel && <Check size={13} style={{
                                                color: 'var(--ds-primary)'
                                            }} />}
                                        </div>
                                        <div className="ds-pick-chip-meta" style={{
                                            marginTop: 2,
                                            fontFamily: 'var(--ds-font-mono)'
                                        }}>
                                            {api.url || '—'}
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </DialogContent>
            </Dialog>

            {/* The existing M51 ApiForm renders its own Dialog. */}
            {formOpen && (
                <ApiForm
                    api={formInitial}
                    onSave={handleFormSave}
                    onCancel={() => setFormOpen(false)}
                    open={formOpen}
                    setOpen={setFormOpen}
                />
            )}
            {busy && (
                <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" /> Saving…
                </div>
            )}
        </div>
    );
}

function blankApi() {
    return {
        name: '',
        url: '',
        method: 'GET',
        timeout: 30,
        headers: {},
        paramsDef: [],
        auth: {
            type: 'none'
        },
        input_schema: '',
        output_schema: ''
    };
}

function prepareForEdit(api) {
    // ApiForm expects schemas as JSON strings; the list view often pre-stringifies
    // them already, but be defensive.
    return {
        ...api,
        input_schema: typeof api.input_schema === 'string'
            ? api.input_schema
            : (api.input_schema ? JSON.stringify(api.input_schema, null, 2) : ''),
        output_schema: typeof api.output_schema === 'string'
            ? api.output_schema
            : (api.output_schema ? JSON.stringify(api.output_schema, null, 2) : '')
    };
}

function ApiChipSkeleton() {
    return (
        <div className="space-y-2 py-0.5" aria-hidden>
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-3 w-full max-w-md" />
        </div>
    );
}
